//================================================================
//  Component: Reassign Approver Pane 
//================================================================

//  Purpose: This side pane allows you to reassign an approver

//  Properties:
//    - paneOpen = { useState, holds a boolean value that opens and closes the sidePane}
//    - setPaneOpen = { useState, used to toggle the visibility of this side pane}
//    - approver = { object, holds information about the selected approver }
//    - onClose = { function, handles closing the pane }

//  Example:
//  
//    <ReassignApproverPane
//      paneOpen={paneOpen}
//      setPaneOpen={setPaneOpen}
//      approver={approver}
//      onClose={closeReassignPane}
//    ></ReassignApproverPane>

//================================================================

// Libraries
import React, { useContext, useState } from 'react';
import { useForm } from 'react-hook-form';

// Components
import UserSearch from '../../../../../Components/UserSearch/UserSearch';

// Contexts
import { GetUser, SetToast } from '../../../../../Library/GlobalContexts';

// Functions
import WriteDocument from '../../../../../Library/WriteDocument';

// Image

// CSS

export default function ReassignApproverPane({
    paneOpen,
    setPaneOpen,
    approver,
    onClose,
}) {

    //------------------------------------------------------
    //  useState
    //------------------------------------------------------

    // Used to indicate the loading state
    const [loading, setLoading] = useState(false);

    //------------------------------------------------------
    //  useContexts
    //------------------------------------------------------

    const getUser = useContext(GetUser);
    const setToast = useContext(SetToast);

    // --------------------------------------------------
    //  Form State
    // --------------------------------------------------

    // https://react-hook-form.com/get-started
    const {

        register,
        handleSubmit,
        reset,
        clearErrors,
        setError,
        setValue,
        watch,
        formState: { errors },

    } = useForm();

    // Watch the required fields to determine if form is complete
    const watchedApprover = watch('approver');
    const watchedJustification = watch('justification');

    // Check if form is ready to submit
    const isFormValid = watchedApprover && watchedJustification && !Object.keys(errors).length;

    //------------------------------------------------------
    //  Functions
    //------------------------------------------------------

    // Submit Handler
    const onSubmit = (data) => {

        // Open the toast with a pending message
        setToast({
            type: 'pending',
            message: 'Reassigning Approver...',
        });

        setLoading(true);

        //---------------------------------------
        //  Update Docs in Firestore
        //---------------------------------------

        const docPromises = [];

        // Construct the document id for the approverhistory collection
        const documentId = `${approver?.approvalbucket.toLowerCase().replace(/\s+/g, '')}-${approver?.role.toLowerCase().replace(/\s+/g, '')}-${approver?.approveremail}-${Date.now()}`;

        // Update the approver in approvers collection
        const updatedApprover = {
            'approveremail': data?.approver,
            'approvergivenname': data?.approvergivenname,
            'approversurname': data?.approversurname,
            'lastmodifieddate': new Date(),
            'lastmodifiedby': {
                'email': getUser?.emailaddress,
                'givenname': getUser?.givenname,
                'surname': getUser?.surname,
            },
        };

        // Update the approver document with new approver
        docPromises.push(
            WriteDocument('approvers', approver?.approverid, updatedApprover, true),
        );

        // Construct the approverhistory document
        const approverHistoryDoc = {
            'itemid': documentId,
            'previousapprover': {
                'email': approver?.approveremail,
                'givenname': approver?.approvergivenname,
                'surname': approver?.approversurname,
            },
            'newapprover': {
                'email': data?.approver,
                'givenname': data?.approvergivenname,
                'surname': data?.approversurname,
            },
            'actionedby': {
                'email': getUser?.emailaddress,
                'givenname': getUser?.givenname,
                'surname': getUser?.surname,
            },
            'actioneddate': new Date(),
            'justification': data?.justification || '',
            'role': approver?.role,
            'approvalbucket': approver?.approvalbucket,
        };

        // Add record to 'approverhistory' collection
        docPromises.push(
            WriteDocument('approverhistory', approverHistoryDoc.itemid, approverHistoryDoc),
        );

        //---------------------------------------
        //  Resolve Promises
        //---------------------------------------

        return Promise.all(docPromises)
        .then(() => {

            // Reset Form & Close Pane
            reset();
            onClose();
            setLoading(false);
            setPaneOpen(false);

            // Update the toast to show success
            setToast({
                type: 'success',
                message: 'Approver reassigned successfully!',
            });

        })
        .catch((error => {

            console.log('error', error);

            // Reset Form & Close Pane
            reset();
            onClose();
            setLoading(false);
            setPaneOpen(false);

            // Update the toast to show an error message
            setToast({
                type: 'error',
                message: 'Failed to reassign approver. Please try again later.',
            });

        }))

    };

    //------------------------------------------------------
    //  HTML
    //------------------------------------------------------

    // Hide Side Pane
    if (!paneOpen) return null;

    // Show Side Pane
    return (
        <div className='Pane-Background'>
            <dialog className='Pane-Container'>
                <form
                    className='grid grid-rows-[70px_1fr_70px] items-center justify-between w-full overflow-hidden'
                    onSubmit={handleSubmit(onSubmit)}
                >

                    {/* Header */}
                    <div className='flex flex-col gap-2 p-4'>
                        <h4 className='text-[22px] p-0 m-0'> Reassign Approver </h4>
                    </div>

                    {/* Body */}
                    <div className='w-[50vw] h-full flex flex-col overflow-y-auto p-4 px-4 py-2 border-solid border shadow-inner'>

                        {/* =================================================== */}
                        {/*  Approver                                           */}
                        {/* =================================================== */}

                        <div className='Form-Row'>
                            <label className='font-medium'> Approver </label>
                            <input
                                className='Input-Field-Text w-[375px]'
                                value={approver?.approveremail}
                                type='text'
                                disabled
                            ></input>
                        </div>

                        {/* =================================================== */}
                        {/*  Role                                               */}
                        {/* =================================================== */}

                        <div className='Form-Row'>
                            <label className='font-medium'> Role </label>
                            <input
                                className='Input-Field-Text w-[375px]'
                                value={approver?.role}
                                type='text'
                                disabled
                            ></input>
                        </div>

                        {/* =================================================== */}
                        {/*  Reassign request to:                               */}
                        {/* =================================================== */}

                        <div className='Form-Row'>
                            <label className='font-medium'> Reassign request to: <span className='text-[#C4314B]'>*</span> </label>
                            <div className='w-[55vh]'>
                                <UserSearch
                                    dataField={'approver'}
                                    required={true}
                                    register={register}
                                    clearErrors={clearErrors}
                                    setError={setError}
                                    setValue={setValue}
                                    errors={errors.approver}
                                ></UserSearch>
                            </div>
                        </div>

                        {/* =================================================== */}
                        {/*  Justification:                                     */}
                        {/* =================================================== */}

                        <div className='Form-Row'>
                            <label className='font-medium'> Justification: <span className='text-[#C4314B]'>*</span> </label>
                            <textarea
                                className={errors.justification ? ('Input-Field-TextArea-Error') : ('Input-Field-TextArea')}
                                {...register('justification', { required: 'Justification is required' })}
                                placeholder='Enter justification'
                            ></textarea>
                            {
                                errors.justification &&
                                <span className='font-medium text-sm text-[#DE0000]'>
                                    {errors.justification.message}
                                </span>
                            }
                        </div>

                    </div>

                    {/* Footer */}
                    <div className='px-4 py-3'>

                        <div className='flex flex-row gap-2 m-0'>
                            {/* Submit */}
                            <button className='Primary-Button whitespace-nowrap' disabled={loading || !isFormValid} type='submit' value='submit'>
                                Submit
                            </button>

                            {/* Cancel */}
                            <button className='Secondary-Button whitespace-nowrap' onClick={() => {

                                // Reset Form & Close Side Pane
                                reset();
                                onClose()

                            }}>
                                Cancel
                            </button>
                        </div>
                    </div>

                </form>
            </dialog>
        </div>
    );
}